/**
 * UpsellStep — visually differentiated upsell between two past-life tiers.
 *
 * Text-only (99⭐):   All 12 past lives as rich narrative text.
 * Portraits (299⭐):  Same narratives PLUS an AI-generated portrait per life.
 *
 * Before opening the 99⭐ PaymentSheet, shows the soft content warning copy
 * (pastLives.ageGate.soft) — same phrasing the AgeGateModal uses for
 * under-18 soft warning, but shown inline before the text purchase.
 *
 * The portraits tier transitions to the existing consent/upload path.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSkuInvoice } from '../../hooks/useSkuInvoice';
import { PaymentSheet } from '../payment/PaymentSheet';
import { useAuth } from '../../store/auth';
import { useImageFeatures } from '../../hooks/useImageFeatures';
import { discoverSkuRunIds, getBaselineSkuRunId } from '../../lib/discover-sku-run';

interface UpsellStepProps {
  /** Called after a successful 99⭐ past_lives_text purchase. runId is the SkuRun id for in-app result display. */
  onTextPurchased: (runId?: string) => void;
  /** Called when user chooses the 299⭐ portraits path — enters existing flow. */
  onPortraitTier: () => void;
  onBack: () => void;
}

/** Example portrait card visual — blurred to tease the portraits tier. */
function BlurredPortraitCard(): JSX.Element {
  return (
    <div
      data-testid="past-lives-blurred-portrait"
      aria-hidden="true"
      style={{
        width: '100%',
        aspectRatio: '3 / 4',
        borderRadius: 16,
        background: 'linear-gradient(160deg, rgba(80,60,150,0.6) 0%, rgba(40,30,80,0.8) 60%, rgba(100,70,30,0.5) 100%)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-end',
        padding: 16,
        position: 'relative',
        overflow: 'hidden',
        border: '1px solid rgba(160,140,220,0.3)',
      }}
    >
      {/* Simulated portrait silhouette */}
      <div style={{
        position: 'absolute',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 72,
        opacity: 0.25,
        filter: 'blur(2px)',
      }}>
        ♾
      </div>
      {/* Blur overlay — signals that portraits are locked */}
      <div style={{
        position: 'absolute',
        inset: 0,
        backdropFilter: 'blur(6px)',
        background: 'rgba(10,8,25,0.35)',
      }} />
      {/* Caption at the bottom */}
      <div style={{
        position: 'relative',
        zIndex: 1,
        textAlign: 'center',
        color: 'rgba(245,233,199,0.9)',
        fontSize: 13,
        lineHeight: 1.4,
        fontStyle: 'italic',
      }}>
        Древний Египет · Жрица Исиды
      </div>
    </div>
  );
}

export function UpsellStep({
  onTextPurchased,
  onPortraitTier,
  onBack,
}: UpsellStepProps): JSX.Element {
  const { t } = useTranslation();
  const { purchase, redeemCoupon } = useSkuInvoice<{ src: 'miniapp' }>();
  const jwt = useAuth((s) => s.jwt);
  const { enabled: imagesOn } = useImageFeatures();

  const [step, setStep] = useState<'choose' | 'soft-warn' | 'payment'>('choose');
  const [isPaying, setIsPaying] = useState(false);

  const handleTextTierClick = (): void => {
    // Surface the soft content warning first, then proceed to PaymentSheet
    setStep('soft-warn');
  };

  const handleSoftWarnConfirm = (): void => {
    setStep('payment');
  };

  // Resolve the freshly-created SkuRun id after payment. Mock-pay returns runIds
  // directly; real Stars dispatches settlePastLivesText asynchronously (empty
  // runIds), so we capture a pre-purchase baseline and discover the new run —
  // the same pattern the portraits tier uses in PastLivesFlow.
  const resolveRunId = async (
    runIdsFromResult: string[] | undefined,
    baselineRunId: string | null,
  ): Promise<string | undefined> => {
    let firstRunId = runIdsFromResult?.[0] ?? undefined;
    if (!firstRunId && jwt) {
      const ids = await discoverSkuRunIds(jwt, 'past_lives_text', baselineRunId);
      firstRunId = ids?.runIds[0] ?? undefined;
    }
    return firstRunId;
  };

  const handlePay = async (): Promise<void> => {
    setIsPaying(true);
    try {
      const baselineRunId = jwt ? await getBaselineSkuRunId(jwt, 'past_lives_text') : null;
      const result = await purchase('past_lives_text', { src: 'miniapp' } as unknown as { src: 'miniapp' });
      if (result.status === 'paid') {
        onTextPurchased(await resolveRunId(result.runIds, baselineRunId));
      }
    } catch {
      // error handled by invoiceState
    } finally {
      setIsPaying(false);
    }
  };

  const handleApplyCoupon = async (code: string): Promise<void> => {
    const baselineRunId = jwt ? await getBaselineSkuRunId(jwt, 'past_lives_text') : null;
    const result = await redeemCoupon('past_lives_text', code, { src: 'miniapp' } as unknown as { src: 'miniapp' });
    if (result.status === 'paid') {
      onTextPurchased(await resolveRunId(result.runIds, baselineRunId));
    }
  };

  // ── Soft content warning ──────────────────────────────────────────────────
  if (step === 'soft-warn') {
    return (
      <div data-testid="past-lives-soft-warn" style={{ padding: '24px 20px', maxWidth: 440, margin: '0 auto' }}>
        <h2 style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--cream)', marginBottom: 12 }}>
          {t('pastLives.ageGate.soft.title', { defaultValue: 'Прежде чем открыть врата' })}
        </h2>
        <p style={{ fontSize: 14, lineHeight: 1.6, color: 'rgba(245,233,199,0.8)', marginBottom: 24 }}>
          {t('pastLives.ageGate.soft.body', {
            defaultValue: 'Хроники прошлых жизней могут затрагивать суровые исторические темы — войны, утраты, тяжкий труд. Продолжайте, только если готовы.',
          })}
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <button
            type="button"
            data-testid="past-lives-soft-warn-confirm"
            style={{
              width: '100%',
              padding: '13px 16px',
              background: 'linear-gradient(135deg, rgba(180,140,90,0.25), rgba(140,100,60,0.35))',
              border: '1px solid rgba(200,165,100,0.4)',
              borderRadius: 12,
              color: 'var(--cream)',
              fontSize: 15,
              fontWeight: 600,
              cursor: 'pointer',
            }}
            onClick={handleSoftWarnConfirm}
          >
            {t('pastLives.ageGate.soft.confirm', { defaultValue: 'Понимаю · продолжить' })}
          </button>
          <button
            type="button"
            data-testid="past-lives-soft-warn-cancel"
            style={{
              width: '100%',
              padding: '11px 16px',
              background: 'none',
              border: '1px solid rgba(255,255,255,0.12)',
              borderRadius: 12,
              color: 'rgba(245,233,199,0.6)',
              fontSize: 14,
              cursor: 'pointer',
            }}
            onClick={() => setStep('choose')}
          >
            {t('pastLives.ageGate.cancel', { defaultValue: 'Не сейчас' })}
          </button>
        </div>
      </div>
    );
  }

  // ── PaymentSheet for 99⭐ text tier ──────────────────────────────────────
  if (step === 'payment') {
    return (
      <PaymentSheet
        skuCode="past_lives_text"
        title={t('pastLives.upsell.textTier.title', { defaultValue: '12 хроник прошлых жизней' })}
        subtitle={t('pastLives.upsell.textTier.subtitle', { defaultValue: 'Глубокие нарративы без портретов' })}
        priceStars={99}
        bullets={[
          t('pastLives.invoice.bullet.lives', { count: 12, defaultValue: '12 past lives, each with narrative' }),
          t('pastLives.invoice.bullet.hero', { defaultValue: 'One Hero life — the closest karmic echo' }),
          t('pastLives.invoice.bullet.refund', { defaultValue: 'Full refund if anything goes wrong' }),
        ]}
        isPaying={isPaying}
        onPay={() => void handlePay()}
        onApplyCoupon={handleApplyCoupon}
        onCancel={() => setStep('choose')}
        testId="past-lives-text-payment-sheet"
      />
    );
  }

  // ── Tier comparison (default: step === 'choose') ───────────────────────────
  return (
    <div data-testid="past-lives-upsell" style={{ padding: '20px 16px', maxWidth: 440, margin: '0 auto' }}>
      <button
        type="button"
        data-testid="past-lives-upsell-back"
        style={{ background: 'none', border: 'none', color: 'rgba(245,233,199,0.6)', fontSize: 13, cursor: 'pointer', padding: '0 0 16px' }}
        onClick={onBack}
      >
        ‹ {t('common:actions.back', { defaultValue: 'Назад' })}
      </button>

      <h2 style={{ fontSize: '1.15rem', fontWeight: 700, color: 'var(--cream)', marginBottom: 6, lineHeight: 1.3 }}>
        {t('pastLives.upsell.title', { defaultValue: 'Выберите свой путь' })}
      </h2>
      <p style={{ fontSize: 13, opacity: 0.6, marginBottom: 24, color: 'var(--cream-dim)' }}>
        {t('pastLives.upsell.subtitle', { defaultValue: 'Все 12 хроник — с текстом или с портретами' })}
      </p>

      {/* ── Side-by-side tier comparison ── */}
      <div style={{ display: 'grid', gridTemplateColumns: imagesOn ? '1fr 1fr' : '1fr', gap: 12, marginBottom: 24 }}>
        {/* Text tier card */}
        <div
          data-testid="past-lives-upsell-text-card"
          style={{
            background: 'rgba(255,255,255,0.04)',
            border: '1px solid rgba(200,165,100,0.25)',
            borderRadius: 14,
            padding: '14px 12px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <div style={{ fontSize: 36, lineHeight: 1 }}>📜</div>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--cream)', textAlign: 'center', lineHeight: 1.3 }}>
            {t('pastLives.upsell.textTier.label', { defaultValue: 'Текст' })}
          </div>
          <div style={{ fontSize: 11, opacity: 0.55, color: 'var(--cream-dim)', textAlign: 'center', lineHeight: 1.4 }}>
            {t('pastLives.upsell.textTier.description', { defaultValue: '12 хроник · глубокие нарративы' })}
          </div>
          <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--cream)' }}>99 ⭐</div>
        </div>

        {/* Portrait tier card — visual premium feel */}
        {imagesOn && (
          <div
            data-testid="past-lives-upsell-portrait-card"
            style={{
              background: 'linear-gradient(160deg, rgba(100,80,180,0.18), rgba(80,60,150,0.25))',
              border: '1px solid rgba(160,140,220,0.4)',
              borderRadius: 14,
              padding: '14px 12px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 8,
              position: 'relative',
            }}
          >
            {/* "Premium" badge */}
            <div style={{
              position: 'absolute',
              top: -8,
              right: 10,
              background: 'linear-gradient(135deg, rgba(160,140,220,0.9), rgba(120,100,200,0.9))',
              borderRadius: 8,
              padding: '2px 8px',
              fontSize: 10,
              fontWeight: 700,
              color: '#fff',
              letterSpacing: '0.05em',
            }}>
              PREMIUM
            </div>

            <BlurredPortraitCard />

            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--cream)', textAlign: 'center', lineHeight: 1.3 }}>
              {t('pastLives.upsell.portraitTier.label', { defaultValue: 'Портреты' })}
            </div>
            <div style={{ fontSize: 11, opacity: 0.55, color: 'var(--cream-dim)', textAlign: 'center', lineHeight: 1.4 }}>
              {t('pastLives.upsell.portraitTier.description', { defaultValue: '12 хроник + AI-портрет каждой жизни' })}
            </div>
            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--cream)' }}>299 ⭐</div>
          </div>
        )}
      </div>

      {/* ── CTA buttons ── */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {imagesOn && (
          <button
            type="button"
            data-testid="past-lives-upsell-portrait-cta"
            style={{
              width: '100%',
              padding: '13px 16px',
              background: 'linear-gradient(135deg, rgba(100,80,180,0.3), rgba(80,60,150,0.4))',
              border: '1px solid rgba(160,140,220,0.5)',
              borderRadius: 12,
              color: 'var(--cream)',
              fontSize: 15,
              fontWeight: 600,
              cursor: 'pointer',
            }}
            onClick={onPortraitTier}
          >
            {t('pastLives.upsell.portraitCta', { defaultValue: 'С портретами — 299 ⭐' })}
          </button>
        )}

        <button
          type="button"
          data-testid="past-lives-upsell-text-cta"
          style={{
            width: '100%',
            padding: '11px 16px',
            background: 'rgba(255,255,255,0.04)',
            border: '1px solid rgba(200,165,100,0.25)',
            borderRadius: 12,
            color: 'rgba(245,233,199,0.75)',
            fontSize: 14,
            cursor: 'pointer',
          }}
          onClick={handleTextTierClick}
        >
          {t('pastLives.upsell.textCta', { defaultValue: 'Получить текстом — 99 ⭐' })}
        </button>
      </div>
    </div>
  );
}
